import React, { useState } from 'react';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import Icon from '../Icon';
import { translate } from '../../Services/translation';
import DetailedListBuyForm from './DetailedProductCardComponents/DetailedListBuyForm';
import { ProductCardWithDetailsModel } from '../../Types/ProductCardWithDetailsModel';
import clsx from 'clsx';
import StockStatusPanel from './DetailedProductCardComponents/StockStatusPanel';
import ProductInformationPanel from './DetailedProductCardComponents/ProductInformationPanel';
import TieredPricesPanel from './DetailedProductCardComponents/TieredPricesPanel';
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import BuySampleButton from '../BuyButton/BuySampleButton';
import { HIDE_TIER_PRICES, StockStatusLevel } from '../../constants';
import DetailedListFavoriteButton from './DetailedProductCardComponents/DetailedListFavoriteButton';
import StockStatus from '../../Types/StockStatus';

const ProductCardWithDetailsMobile = ({
  product,
  quantity,
  boxesAmount,
  rollsAmount,
  totalAmount,
  packageSize,
  setQuantity,
  setBoxesAmount,
  setRollsAmount,
  setTotalAmount,
}: ProductCardWithDetailsModel) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const getStockStatus = (stockStatus: StockStatus) => {
    if (!stockStatus.inStock) return product.itemRestockDate;

    return stockStatus.level == StockStatusLevel.OnDemand
      ? stockStatus.description
      : `${stockStatus.quantity} ${translate(
          'detailed-product-card.units-in-stock'
        )}`;
  };

  return (
    <div className="w-full p-3 lg:hidden ">
      <div className="flex gap-3">
        {product.images && product.images.length > 0 ? (
          <a href={product.url} className="w-[66px]">
            <ResponsiveImage
              imgProps={{
                className:
                  'aspect-[3/4] object-cover w-full rounded hover:cursor-pointer transition duration-500 group-hover:scale-105',
              }}
              {...product.images[0]?.imageModel}
            />
          </a>
        ) : (
          <a href={product.url} className="w-[66px]">
            <div className="w-full rounded object-cover transition duration-500 aspect-[3/4] group-hover:scale-105 hover:cursor-pointer"></div>
          </a>
        )}
        <div className="flex-1">
          <div className="mb-0.5 flex justify-between">
            <div>{product.id}</div>
            <DetailedListFavoriteButton product={product} />
          </div>
          <h5 className="mb-0.5">{product.name}</h5>
          <div className="mb-0.5 text-md text-gray-500">
            {product.itemCollection}
          </div>
          <div className="text-md text-gray-500">{`${
            product.itemPackageSize
          } ${product.price.price?.unitOfMeasurementTitle} / ${translate(
            'detailed-product-card.boxes'
          )} `}</div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-md">
          {`${product.price.formattedPrice} / `}
          <span className="text-sm text-gray-500">
            {product.price.price?.unitOfMeasurementTitle}
          </span>
        </div>
        <div>
          {product.stockStatus && getStockStatus(product.stockStatus)}
        </div>
      </div>
      <div className="flex w-full md-down:grid md-down:grid-cols-[1fr_1fr_1fr_1fr] md-down:gap-3">
        <DetailedListBuyForm
          product={product}
          quantity={quantity}
          boxesAmount={boxesAmount}
          rollsAmount={rollsAmount}
          totalAmount={totalAmount}
          packageSize={packageSize}
          setQuantity={setQuantity}
          setBoxesAmount={setBoxesAmount}
          setRollsAmount={setRollsAmount}
          setTotalAmount={setTotalAmount}
        />
      </div>
      <div>
        <button
          className="flex items-center gap-0.5 self-start py-2"
          onClick={() => setDetailsOpen(!detailsOpen)}
        >
          <span className="text-md underline">
            {translate('detailed-product-card.details')}
          </span>
          <Icon
            name="chevron-left"
            className={clsx(
              'h-4 w-4 transition-transform',
              detailsOpen ? 'rotate-90' : 'rotate-[270deg] '
            )}
          />
        </button>
      </div>
      <div
        className={clsx(
          'grid w-full transition-[grid-template-rows] duration-200',
          detailsOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="flex flex-col gap-3 overflow-hidden">
          <div className="rounded border border-gray-200 p-4">
            <StockStatusPanel
              itemBatchInventories={product.itemBatchInventories}
              productId={product.id}
            />
          </div>
          <div className="rounded border border-gray-200 p-4">
            <ProductInformationPanel product={product} />
          </div>
          {product.price?.tierPrices?.length > 0 && !HIDE_TIER_PRICES && (
            <div className="rounded border border-gray-200 p-4">
              <TieredPricesPanel priceModel={product.price} />
            </div>
          )}
          {product.stockStatus?.level !== StockStatusLevel.Inactive &&
            !product.disableBuyOption &&
            product.samples?.length! > 0 &&
            product.samples?.map((sample) => (
              <BuySampleButton
                {...sample}
                className="col-span-4 border border-solid border-gray-700"
                btnSize="medium"
                key={`sample-${sample.articleNumber}`}
              />
            ))}
          <a
            href={product.url}
            className="btn btn--white btn--medium w-full border-gray-700"
          >
            {translate('product-card.visit-product-page')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProductCardWithDetailsMobile;
